import {alias, createModelSchema, custom, deserialize, primitive, serialize, SKIP} from "serializr";
import {Schema} from "@/common/Schema";
import {Cloneable} from "@/common/Cloneable";

import {WorkerConfig} from "../worker/WorkerConfig";
import {RewardModel} from "../reward/RewardModel";
import {GuardConfig} from "../guard/GuardConfig";
import {SearchConfig} from "../search/SearchConfig";
import {Attributes} from "../attribute/Attributes";

import {ItemMergeConfig} from "./merge/ItemMergeConfig"
import {ItemOfferConfig} from "./offer/ItemOfferConfig";
import {ItemCollectMode} from "./ItemCollectMode";
import {ItemUpgradeConfig} from "./upgrade/ItemUpgradeConfig";
import {ItemDeprecateConfig} from "./deprecate/ItemDeprecateConfig";
import {ItemRecycleConfig} from "./recycle/ItemRecycleConfig";
import {ItemTraderConfig} from "@/modules/redactor/models/item/trader/ItemTraderConfig";
import {ItemDragonConfig} from "@/modules/redactor/models/item/dragon/ItemDragonConfig";
import {ItemBoostConfig} from "@/modules/redactor/models/item/boost/ItemBoostConfig";
import {ItemTouchConfig} from "@/modules/redactor/models/item/touch/ItemTouchConfig";
import {ItemContainerMode} from "@/modules/redactor/models/item/ItemContainerMode";
import {ItemInfoConfig} from "@/modules/redactor/models/item/info/ItemInfoConfig";

export class ItemConfig extends Cloneable {

    static schema() {
        return custom(
            (v) => {
                return v && !v.empty() ? serialize(ItemConfig, v) : SKIP;
            },
            (v) => {
                return deserialize(ItemConfig, v);
            }
        );
    }

    constructor() {
        super();
        this._mana = 0;
        this._points = 0;

        this._shape = 0;
        this._move = false;
        this._swap = false;
        this._delete = false;
        this._archive = false;
        this._explode = false;
        this._autoreward = false;
        this._roadThrough = false;
        this._infoEnabled = false;

        this._bubbleReward = false;

        this._collect = ItemCollectMode.DISABLED;
        this._container = ItemContainerMode.DISABLED;

        this._attributes = null;

        this._infoConfig = null;
        this._touchConfig = null;
        this._boostConfig = null;
        this._mergeConfig = null;
        this._guardConfig = null;
        this._offerConfig = null;
        this._searchConfig = null;
        this._workerConfig = null;
        this._traderConfig = null;
        this._dragonConfig = null;
        this._upgradeConfig = null;
        this._recycleConfig = null;
        this._deprecateConfig = null;

        this._rewardOnMerge = null;
        this._rewardOnCreate = null;
        this._rewardOnRemove = null;
        this._rewardOnDelete = null;
        this._rewardOnLevelUp = null;
    }

    get mana() {
        return this._mana;
    }

    set mana(value) {
        this._mana = value;
    }

    get points() {
        return this._points;
    }

    set points(value) {
        this._points = value;
    }

    get shape() {
        return this._shape;
    }

    set shape(value) {
        this._shape = value;
    }

    get move() {
        return this._move;
    }

    set move(value) {
        this._move = value;

        if (!value && this.swap) {
            this.swap = false;
        }
    }

    get swap() {
        return this._swap;
    }

    set swap(value) {
        this._swap = value;
        if (value && !this.move) {
            this.move = true;
        }
    }

    get delete() {
        return this._delete;
    }

    set delete(value) {
        this._delete = value;
    }

    get archive() {
        return this._archive;
    }

    set archive(value) {
        this._archive = value;
    }

    get explode() {
        return this._explode;
    }

    set explode(value) {
        this._explode = value;
    }

    get autoreward() {
        return this._autoreward;
    }

    set autoreward(value) {
        this._autoreward = value;
    }

    get roadThrough() {
        return this._roadThrough;
    }

    set roadThrough(value) {
        this._roadThrough = value;
    }

    get infoEnabled() {
        return this._infoEnabled;
    }

    set infoEnabled(value) {
        this._infoEnabled = value;
    }

    set touchConfig(value) {
        this._touchConfig = value;
    }

    get touchConfig() {
        return this._touchConfig;
    }

    get bubbleReward() {
        return this._bubbleReward;
    }

    set bubbleReward(value) {
        this._bubbleReward = value;
    }

    get attributes() {
        return this._attributes;
    }

    set attributes(value) {
        this._attributes = value;
    }

    get collect() {
        return this._collect;
    }

    set collect(value) {
        this._collect = value;
    }

    get container() {
        return this._container;
    }

    set container(value) {
        this._container = value;
    }

    get searchConfig() {
        return this._searchConfig;
    }

    set searchConfig(value) {
        this._searchConfig = value;
    }

    get workerConfig() {
        return this._workerConfig;
    }

    set workerConfig(value) {
        this._workerConfig = value;
    }

    get infoConfig() {
        return this._infoConfig;
    }

    set infoConfig(value) {
        this._infoConfig = value;
    }

    get boostConfig() {
        return this._boostConfig;
    }

    set boostConfig(value) {
        this._boostConfig = value;
    }

    get mergeConfig() {
        return this._mergeConfig;
    }

    set mergeConfig(value) {
        this._mergeConfig = value;
    }

    get guardConfig() {
        return this._guardConfig;
    }

    set guardConfig(value) {
        this._guardConfig = value;
    }

    get offerConfig() {
        return this._offerConfig;
    }

    set offerConfig(value) {
        this._offerConfig = value;
    }

    get traderConfig() {
        return this._traderConfig;
    }

    set traderConfig(value) {
        this._traderConfig = value;
    }

    get dragonConfig() {
        return this._dragonConfig;
    }

    set dragonConfig(value) {
        this._dragonConfig = value;
    }

    get recycleConfig() {
        return this._recycleConfig;
    }

    set recycleConfig(value) {
        this._recycleConfig = value;
    }

    get upgradeConfig() {
        return this._upgradeConfig;
    }

    set upgradeConfig(value) {
        this._upgradeConfig = value;
    }

    get deprecateConfig() {
        return this._deprecateConfig;
    }

    set deprecateConfig(value) {
        this._deprecateConfig = value;
    }

    get rewardOnMerge() {
        return this._rewardOnMerge;
    }

    set rewardOnMerge(value) {
        this._rewardOnMerge = value;
    }

    get rewardOnCreate() {
        return this._rewardOnCreate;
    }

    set rewardOnCreate(value) {
        this._rewardOnCreate = value;
    }

    get rewardOnRemove() {
        return this._rewardOnRemove;
    }

    set rewardOnRemove(value) {
        this._rewardOnRemove = value;
    }

    get rewardOnDelete() {
        return this._rewardOnDelete;
    }

    set rewardOnDelete(value) {
        this._rewardOnDelete = value;
    }

    get rewardOnLevelUp() {
        return this._rewardOnLevelUp;
    }

    set rewardOnLevelUp(value) {
        this._rewardOnLevelUp = value;
    }

    valid() {

        if (!ItemCollectMode.is(this.collect)) {
            return false;
        }

        if (!ItemContainerMode.is(this.container)) {
            return false;
        }

        if (!Number.isInteger(this.mana) || this.mana < 0) {
            return false;
        }

        if (!Number.isInteger(this.shape) || this.shape < 0 || this.shape > 4) {
            return false;
        }

        if (!Number.isInteger(this.points) || this.points < 0) {
            return false;
        }

        if (this.searchConfig && !this.searchConfig.valid()) {
            return false;
        }

        if (this.infoConfig && !this.infoConfig.valid()) {
            return false;
        }

        if (this.mergeConfig && !this.mergeConfig.valid()) {
            return false;
        }

        if (this.guardConfig && !this.guardConfig.valid()) {
            return false;
        }

        if (this.offerConfig && !this.offerConfig.valid()) {
            return false;
        }

        if (this.workerConfig && !this.workerConfig.valid()) {
            return false;
        }

        if (this.traderConfig && !this.traderConfig.valid()) {
            return false;
        }

        if (this.dragonConfig && !this.dragonConfig.valid()) {
            return false;
        }

        if (this.upgradeConfig && !this.upgradeConfig.valid()) {
            return false;
        }

        if (this.recycleConfig && !this.recycleConfig.valid()) {
            return false;
        }

        if (this.deprecateConfig && !this.deprecateConfig.valid()) {
            return false;
        }

        if (this.touchConfig && !this.touchConfig.valid()) {
            return false;
        }

        if (this.rewardOnMerge && !this.rewardOnMerge.valid()) {
            return false;
        }

        if (this.rewardOnCreate && !this.rewardOnCreate.valid()) {
            return false;
        }

        if (this.rewardOnRemove && !this.rewardOnRemove.valid()) {
            return false;
        }

        if (this.rewardOnDelete && !this.rewardOnDelete.valid()) {
            return false;
        }

        if (this.rewardOnLevelUp && !this.rewardOnLevelUp.valid()) {
            return false;
        }

        if (this.attributes && !this.attributes.valid()) {
            return false;
        }

        return true;
    }

    empty() {
        return false;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new ItemConfig());
        } else if (target instanceof ItemConfig) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(ItemConfig, {
    _mana: alias("mana", Schema.primitive()),
    _shape: alias("shape", Schema.primitive()),
    _points: alias("upgrade_points", Schema.primitive()),

    _move: alias("move", primitive()), /* don't change to Schema.primitive() */
    _swap: alias("swap", primitive()), /* don't change to Schema.primitive() */
    _delete: alias("delete", primitive()), /* don't change to Schema.primitive() */
    _archive: alias("archive", primitive()), /* don't change to Schema.primitive() */
    _explode: alias("explode", Schema.primitive()),
    _autoreward: alias("autoreward", primitive()), /* don't change to Schema.primitive() */
    _roadThrough: alias("road_through", Schema.primitive()),
    _infoEnabled: alias("info_enabled", Schema.primitive()),
    _bubbleReward: alias("bubble_reward", Schema.primitive()),
    _collect: alias("collect", ItemCollectMode.schema()),
    _container: alias("container", ItemContainerMode.schema()),

    _attributes: alias("attributes", Attributes.schema()),

    _searchConfig: alias("find_config", SearchConfig.schema()),

    _infoConfig: alias("info_config", ItemInfoConfig.schema()),
    _touchConfig: alias("touch_config", ItemTouchConfig.schema()),
    _boostConfig: alias("boost_config", ItemBoostConfig.schema()),
    _mergeConfig: alias("merge_config", ItemMergeConfig.schema()),
    _guardConfig: alias("guard_config", GuardConfig.schema()),
    _offerConfig: alias("offer_config", ItemOfferConfig.schema()),
    _workerConfig: alias("worker_config", WorkerConfig.schema()),
    _traderConfig: alias("trader_config", ItemTraderConfig.schema()),
    _dragonConfig: alias("dragon_config", ItemDragonConfig.schema()),
    _upgradeConfig: alias("upgrade_config", ItemUpgradeConfig.schema()),
    _recycleConfig: alias("recycle_config", ItemRecycleConfig.schema()),
    _deprecateConfig: alias("deprecate_config", ItemDeprecateConfig.schema()),

    _rewardOnMerge: alias("reward_merge", RewardModel.schema()),
    _rewardOnCreate: alias("reward_create", RewardModel.schema()),
    _rewardOnRemove: alias("reward_remove", RewardModel.schema()),
    _rewardOnDelete: alias("reward_delete", RewardModel.schema()),
    _rewardOnLevelUp: alias("reward_levelup", RewardModel.schema())
});
